import { Component, Input, ViewChild, ElementRef, OnInit, ViewEncapsulation } from '@angular/core';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { TaskPage } from 'src/pages/tasks/task/task';
import { Client } from 'src/providers/client/client';
import { ReportIssue } from 'src/providers/report-issue/report-issue';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TasksService } from 'src/providers/tasks/tasks';
import { Util } from 'src/shared/util/util';

@Component({
  selector: 'tidy-task-dropdown',
  templateUrl: './task-dropdown.html',
  styleUrls: ['./task-dropdown.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class TaskDropdownComponent implements OnInit {

  filteredTasks: any[] = [];
  selectedTaskIds: number[] = [];
  searchValue = '';
  @ViewChild('taskMenu') taskMenu: MatMenu;
  @ViewChild('menuTrigger') menuTrigger: MatMenuTrigger;
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
  @Input() bookingId: number;
  @Input() addressId: number;
  @Input() jobId: number;
  @Input() priorPage: string;
  @Input() priorPageParams: string;
  @Input() itemId: any;
  @Input() showTrigger = true;
  dialogParams: any;

  constructor(
    private client: Client,
    private reportIssue: ReportIssue,
    private storage: TidyStorage,
    private windowService: WindowService,
    private rightSidePanelService: RightSidePanelService,
    private navCtrl: CustomNavController,
    public tasksService: TasksService,
    private util: Util
  ) {}

  async ngOnInit() {
    this.dialogParams = await this.storage.retrieve('dialog-params');
    await this.loadTasksForJob();
  }

  openDropdown() {
    if (this.menuTrigger) {
      this.menuTrigger.openMenu();
    }
  }

  onMenuOpened() {
    this.searchValue = '';
    this.filteredTasks = [...this.tasksService.allTasks];
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
    }, 100);
  }

  onMenuClosed() {
    this.searchValue = '';
    this.filteredTasks = [...this.tasksService.allTasks];
  }

  onSearchInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchValue = input.value;
    const searchLower = input.value.toLowerCase();
    if (!searchLower) {
      this.filteredTasks = [...this.tasksService.allTasks];
    } else {
      this.filteredTasks = this.tasksService.allTasks.filter((task) =>
        (task.title && task.title.toLowerCase().includes(searchLower)) ||
        (task.description && task.description.toLowerCase().includes(searchLower))
      );
    }
  }

  clearSearch() {
    this.searchValue = '';
    this.filteredTasks = [...this.tasksService.allTasks];
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.focus();
    }
  }

  async toggleTask(task: any) {
    if (task.checked) {
      await this.uncheckTask(task);
    } else {
      await this.checkTask(task);
    }
  }

  async checkTask(task: any) {
    try {
      const payload = {
        issue_report_id: task.id,
        attachable_id: this.priorPage === 'item-details' ? this.itemId : this.bookingId,
        attachable_type: this.priorPage === 'item-details' ? 'RoomObject' : 'Booking'
      };
      const response = await this.reportIssue.addIssueReportAttachment(payload);
      task['attachmentId'] = response.attachment_id || response.id;
      task['checked'] = true;
      if (!this.tasksService.linkedTasks.find(t => t.id === task.id)) {
        this.tasksService.linkedTasks.push(task);
      }
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  async uncheckTask(task: any) {
    try {
      if (task.attachmentId) {
        await this.reportIssue.deleteIssueReportAttachment(task.attachmentId);
        delete task['attachmentId'];
        task['checked'] = false;
        const index = this.tasksService.linkedTasks.findIndex(t => t.id === task.id);
        if (index > -1) {
          this.tasksService.linkedTasks.splice(index, 1);
        }
      }
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  async loadTasksForJob() {
    try {
      this.tasksService.tasksLoaded = false;
      const tasksResponse = await this.reportIssue.getIssueReportsByAddress(this.addressId);
      this.tasksService.allTasks = tasksResponse.filter(task => !task.archived_at);
      this.filteredTasks = [...this.tasksService.allTasks];
      if (this.priorPage === 'item-details') {
        this.tasksService.allTasks.forEach(task => {
          const linkedItem = task.room_objects?.find(item => item.id === this.itemId);
          task['checked'] = !!linkedItem;
          if (linkedItem) {
            task['attachmentId'] = linkedItem.attachment_id;
          }
        });
      } else {
        this.tasksService.allTasks.forEach(task => {
          const linkedBooking = task.bookings?.find(booking => booking.id === this.bookingId);
          task['checked'] = !!linkedBooking;
          if (linkedBooking) {
            task['attachmentId'] = linkedBooking.attachment_id;
          }
        });
      }
      this.selectedTaskIds = this.tasksService.allTasks.filter(t => t.checked).map(t => t.id);
      this.tasksService.linkedTasks = this.tasksService.allTasks.filter(task => task.checked);
      this.tasksService.tasksLoaded = true;
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
      this.tasksService.tasksLoaded = true;
    }
  }

  async goToAddTask() {
    const params = {
      cameFromJobPage: true,
      issue: null,
      addressResponse: [await this.client.getAddresses()].flat(),
      issueReportTypes: await this.reportIssue.getIssueReportTypes(),
      addressId: this.addressId,
      jobId: this.jobId,
      bookingId: this.bookingId,
      priorPage: this.priorPage,
      priorPageParams: this.priorPageParams
    }
    this.dialogParams = { ...this.dialogParams, ...params };
    await this.storage.save('dialog-params', this.dialogParams);
    if (this.windowService.isDesktopRes) {
      await this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.navigateTo('task', params, TaskPage, false);
    } else {
      this.navCtrl.navigateForward('task', params);
    }
  }
}
